import React, { useRef, useEffect, useCallback, useState } from 'react';
import { Bar, Button, ResponsivePopover, List, ListItemStandard, Dialog, Icon } from '@ui5/webcomponents-react';
import { useNavigate, useLocation } from 'react-router-dom';
import Breadcrumbs from './Breadcrumbs';
import "@ui5/webcomponents-icons/dist/palette.js";
import { applyTheme, getResolvedTheme, THEMES } from '../utils/theme';
import './Navbar.css';

function Navbar({ toggleSidebar }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [currentTheme, setCurrentTheme] = useState(() => getResolvedTheme());
  const popoverOpenerRef = useRef(null);
  const [userInfo, setUserInfo] = useState(null);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false); // New state for logout confirmation

  const handleLogout = useCallback(() => {
    localStorage.clear();
    navigate('/login');
    setShowLogoutConfirm(false);
  }, [navigate]);

  const openLogoutConfirm = useCallback(() => {
    setShowLogoutConfirm(true);
  }, []);

  const handleThemeSettingsToggle = useCallback((event) => {
    popoverOpenerRef.current = event.currentTarget;
    setCurrentTheme(getResolvedTheme());
    setPopoverOpen(true);
  }, []);

  const handleThemeChange = useCallback((event) => {
    const selectedItem = event.detail?.targetItem || event.detail?.selectedItems?.[0];
    if (!selectedItem) return;
    const selectedTheme = selectedItem.dataset?.key || selectedItem.getAttribute('data-key');
    if (!selectedTheme) return;
    const appliedTheme = applyTheme(selectedTheme);
    setCurrentTheme(appliedTheme);
    setPopoverOpen(false);
  }, []);

  useEffect(() => {
    window.addEventListener('faros:request-logout', openLogoutConfirm);

    return () => {
      window.removeEventListener('faros:request-logout', openLogoutConfirm);
    };
  }, [openLogoutConfirm]);

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      // Most browsers will ignore the custom message and display a generic one.
      // However, returning any non-null, non-empty value will trigger the confirmation.
      event.returnValue = '¿Está seguro que desea cerrar sesión?';
      return event.returnValue;
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []); // Empty dependency array means this effect runs once on mount and cleans up on unmount

  useEffect(() => {
    // Obtener información del usuario desde localStorage
    const userData = localStorage.getItem('userData');
    const userCompanyDisplay = localStorage.getItem('userCompanyDisplay');

    if (userData) {
      try {
        const parsedUserData = JSON.parse(userData);
        // Add the company/supplier display name to userInfo
        setUserInfo({
          ...parsedUserData,
          companyDisplay: userCompanyDisplay || ''
        });
      } catch (error) {
        console.error('Error parsing user data:', error);
      }
    }
  }, []);

  return (
    <>
      <div style={{ 
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1000,
        backgroundColor: '#354a5f',
        minWidth: '100%'
      }}>
        <div style={{ 
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          height: '48px'
          }}>
          {/* Contenido izquierdo */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem'}}>
            <Button 
              icon="menu2"
              design="Transparent"
              style={{ color: 'white' }}
              onClick={toggleSidebar}
            />
            <img 
              src="https://ucarecdn.com/c793b2a4-2e28-4a70-be42-19e80089c767/LogotipoFAROS_nuevo1_blanco.png"
              alt="Faros Logo"
              style={{ 
                height: '30px',
                cursor: 'pointer'
              }}
              onClick={() => navigate('/home', { state: { activePart: 'home' } })}
            />
          </div>

          {/* Contenido central - Información del usuario */}
          {userInfo && (
            <div style={{ 
              position: 'absolute',
              left: '50%',
              transform: 'translateX(-50%)',
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center',
              color: 'white',
              fontSize: '0.875rem'
            }}>
              {userInfo.username || ''} • {userInfo.role?.roleName || ''} • {userInfo.companyDisplay || ''}
            </div>
          )}

          {/* Contenido derecho */}
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <Button 
              icon="palette"
              design="Transparent" 
              style={{ color: 'white' }}
              onClick={handleThemeSettingsToggle}
            />
            <Button icon="search" design="Transparent" style={{ color: 'white' }} />
            <Button icon="settings" design="Transparent" style={{ color: 'white' }} />
            <Button icon="bell" design="Transparent" style={{ color: 'white' }} />
            <Button 
              icon="log" 
              design="Transparent" 
              style={{ color: 'white' }}
              onClick={openLogoutConfirm}
            />
          </div>
        </div>
         {location.pathname !== '/home' &&
          !location.pathname.startsWith('/sales-order-detail') && (
        <Bar
          style={{
            height: '30px',
            zIndex: 2000
          }} 
          startContent={
            <div style={{ display: 'flex', alignItems: 'center', marginLeft:'60px'}}>
            <Breadcrumbs />
            </div>}
        />
         )}
      </div>

      <ResponsivePopover
        open={popoverOpen}
        opener={popoverOpenerRef.current}
        onAfterClose={() => setPopoverOpen(false)}
        headerText="Tema"
        placement="Bottom"
        horizontalAlign="End"
      >
        <List
          onSelectionChange={handleThemeChange}
          selectionMode="Single"
        >
          {THEMES.map((theme) => (
            <ListItemStandard
              key={theme.key}
              icon="palette"
              data-key={theme.key}
              selected={currentTheme === theme.key}
            >
              {theme.value}
            </ListItemStandard>
          ))}
        </List>
      </ResponsivePopover>

      <Dialog
        open={showLogoutConfirm}
        onAfterClose={() => setShowLogoutConfirm(false)}
        className="logout-confirm-dialog"
      >
        <div className="logout-confirm">
          <Button
            className="logout-confirm__close"
            design="Transparent"
            icon="decline"
            tooltip="Cerrar"
            onClick={() => setShowLogoutConfirm(false)}
          />

          <div className="logout-confirm__icon">
            <Icon name="log" />
          </div>

          <div className="logout-confirm__content">
            <h2>Confirmar Cierre de Sesión</h2>
            <p>¿Estás seguro de que deseas cerrar tu sesión?</p>
            <span>Serás redirigido a la página de inicio de sesión.</span>
          </div>

          <div className="logout-confirm__actions">
            <Button
              className="logout-confirm__cancel"
              design="Transparent"
              icon="decline"
              onClick={() => setShowLogoutConfirm(false)}
            >
              Cancelar
            </Button>
            <Button
              className="logout-confirm__submit"
              design="Negative"
              icon="log"
              onClick={handleLogout}
            >
              Cerrar Sesión
            </Button>
          </div>
        </div>
      </Dialog>
    </>
  );
}

export default Navbar; 